import gql from 'graphql-tag';

import {
  instantPromotionFields,
  potentialPromotionsFragment
} from './potential-promotions-fragment';
import {
  markdownPriceDataFragment,
  swatchImageDataAttributes
} from './product-fragments';

import { associatedSetFragment } from '../graphql/product-fragments';

import { PAGE_SIZE, PRODUCT_TYPES } from '../constants/products';

const plpQueriesAlcFields = gql`
        fragment plpQueriesAlcFields on ProductData {
          addToCartDisabled
          articleServiceType
          articleStatus
          articleStatusLabel
          productLaunchDate
        }
      `;

const plpQueriesStoppingFields = gql`
        fragment plpQueriesStoppingFields on StoppingData {
          rollingResistanceRating
          wetRating
          wetDistance
          wetWornDistance
          dryDistance
          dryRating
          winterRating
        }
      `;

const windshieldWiperFields = gql`
  fragment windshieldWiperFields on ProductData {
    accessoryType
    brand
    brandCategory
    fitmentPosition {
      code
      description
    }
  }
`;

export const facetsFragment = gql`
  fragment facetsFragment on FacetData_SearchStateData {
    category
    code
    isSlider
    description
    multiSelect
    name
    needCollapse
    needExpand
    needShowMore
    priority
    topValues {
      code
      count
      name
      query {
        listUrl
        query {
          value
        }
        url
      }
      scaleMaxVal
      scaleMinVal
      selected
      sliderMaxVal
      sliderMinVal
    }
    values {
      code
      count
      name
      plpToolTip
      query {
        listUrl
        query {
          value
        }
        url
      }
      scaleMaxVal
      scaleMinVal
      selected
      sliderMaxVal
      sliderMinVal
    }
    type
  }
`;

export const stockFields = gql`
  fragment stockFields on StockData {
    availableDate
    cityOfStore
    daysOut
    purchaseDecisionValue
    relativeStockCount
    stockCount
    availabilityMessage
    availabilityMessageRank
    preferredStoreStockMessage
    relativeInventory {
      isAvailable
      relativeInventoryStatus
      stockMessagingCode
    }
    vendorsStockCount
    stockLevelStatus {
      code
    }
    storeCode
    storeStreetAddress
  }
`;

const tdsDataFields = gql`
  fragment tdsDataFields on TdsData {
    code
    mileage {
      median
      max
      warranty
      min
      mileageType
    }
    stopping {
      ...plpQueriesStoppingFields
    }
    rideRating {
      handling
      quiet
      comfort
      wetHandling
    }
    manufacturerPartNumber
	  testVehicleType
	  testVehicleCategory
	  durabilityRating
	  dryRating
	  wetWornProxy
	  rollingResistanceProxy
	  sustainabilityRating
	  sustainabilityProxy
    tireLifeMonths
    tireLifeYears
    winter {
      snowBrakeFeet
      snowHandlingRating
    }
  }
  ${plpQueriesStoppingFields}
`;

const plpRecommendationDataFields =
  process.env.__FF_TREADWELL_AI_QUERY_UPDATES__ === 'true'
    ? gql`
        fragment plpRecommendationDataFields on RecommendedArticleData {
          rideRating {
            handling
            quiet
            comfort
            wetHandling
          }
          tireLifeYears
          tireLifeMonths
          totalCost
          totalCostOfOwnershipRating
          stopping {
            ...plpQueriesStoppingFields
          }
          rank
          mileage {
            median
            max
            warranty
            min
          }
          milesPerDollar
          treadwellSessionResponseCacheKey
          winter {
            snowBrakeFeet,
            snowHandlingRating,
            snowBrakeRating
          }
        }
        ${plpQueriesStoppingFields}
      `
    : gql`
        fragment plpRecommendationDataFields on RecommendedArticleData {
          rideRating {
            handling
            quiet
            comfort
            wetHandling
          }
          tireLifeYears
          tireLifeMonths
          totalCost
          totalCostOfOwnershipRating
          stopping {
            ...plpQueriesStoppingFields
          }
          rank
          mileage {
            median
            max
            warranty
            min
          }
          milesPerDollar
          winter {
            snowBrakeFeet,
            snowHandlingRating,
            snowBrakeRating
          }
        }
        ${plpQueriesStoppingFields}
      `;

const treadwellDataFields = gql`
        fragment treadwellDataFields on ProductData {
          tdsData {
            ...tdsDataFields
          }
          recommendationData {
            ...plpRecommendationDataFields
          }
        }
        ${tdsDataFields}
        ${plpRecommendationDataFields}
      `;

const productListFleetPreferredFields =
  process.env.__FF_FLEET_ACCOUNT__ === 'true'
    ? gql`
        fragment productListFleetPreferredFields on ProductData {
          isFleetPreferred
          code
        }
      `
    : gql`
        fragment productListFleetPreferredFields on ProductData {
          code
        }
      `;

export const productFragment = gql`
  fragment productFragment on ProductData {
    ...plpQueriesAlcFields
    ...windshieldWiperFields
    ...treadwellDataFields
    ...productListFleetPreferredFields
    rideComfort
    rideNoise
    corneringSteering
    treadLife
    aggregates {
      allVehiclesReviewCount
      codes
      propertyRanges {
        name
        rangeValue
      }
      itemType
      specialCanonical
    }
    averageRating
    baseprice {
      value
      formattedValue
    }
    brandLogo {
      url
    }
    color
    coreMarketingCode
    description
    freeShipping
    gbb
    gtin
    icons {
      altText
    }
    images {
      url
      altText
      format
    }
    isCoreProduct
    isExclusive
    isOtherVehiclesOE
    isViewableOnVehicle
    isVirtualAssortmentParent
    origEquipmentData {
      description
      disPlayName
      name
    }
    line
    manufacturerAID
    manufacturerMileageWarranty
    mapDisplayRule {
      mapPriceDisplay
      message
      messageDisplay
    }
    mapPrice {
      formattedValue
      value
    }
    mapRuleSatisfied
    name
    potentialPromotions {
      ...potentialPromotionsFragment
    }
    price {
      value
      formattedValue
    }
    priceRange {
      minPrice {
        formattedValue
        value
      }
      maxPrice {
        formattedValue
        value
      }
    }
    productType
    rearProduct {
      ...plpQueriesAlcFields
      ...productListFleetPreferredFields
      coreMarketingCode
      description
      origEquipmentData {
        description
        disPlayName
        name
      }
      gbb
      gtin
      brand
      brandLogo {
        url
      }
      images {
        url
        altText
        format
      }
      isVirtualAssortmentParent
      name
      productType
      potentialPromotions {
        ...potentialPromotionsFragment
      }
      price {
        value
        formattedValue
      }
      mapRuleSatisfied
      mapDisplayRule {
        mapPriceDisplay
        message
        messageDisplay
      }
      mapPrice {
        formattedValue
        value
      }
      size
      stock {
        preferredStoreStockMessage
        purchaseDecisionValue
        relativeStockCount
        stockCount
        availabilityMessage
        availabilityMessageRank
        stockLevelStatus {
          code
        }
      }
      markdownPriceData {
        ...markdownPriceDataFragment
      }
      manufacturerAID
      unitName
      wheelRimDiameter
      wheelWidth
    }
    reviewSummaryData {
      totalCount
      recommendCount
      totalRating
    }
    size
    stock {
      ...stockFields
    }
    unitName
    unitOfMeasureDescription
    url
    wheelFinish: finish
    wheelColor
    wheelRimDiameter
    wheelWidth
    swatches {
      ...swatchImageDataAttributes
    }
    vendorFinish
    canonicalFallBackUrl
    markdownPriceData {
      ...markdownPriceDataFragment
    }
  }
  ${markdownPriceDataFragment}
  ${instantPromotionFields}
  ${potentialPromotionsFragment}
  ${plpQueriesAlcFields}
  ${stockFields}
  ${windshieldWiperFields}
  ${treadwellDataFields}
  ${swatchImageDataAttributes}
  ${productListFleetPreferredFields}
`;

export const productSearchResult = gql`
  fragment productSearchResult on ProductSearchResult {
    facets {
      ...facetsFragment
    }
    pagination {
      currentPage
      numberOfPages
      pageSize
      totalNumberOfResults
    }
    results {
      ...productFragment
    }
    sorts {
      code
      name
      selected
    }
    recommendationAvailable
  }
  ${facetsFragment}
  ${productFragment}
`;

const FITMENT_TIRE_PRODUCT_SEARCH = gql`
  query FitmentTireProductSearch(
    $vehicleInfo: VehicleInput!
    $searchInput: StandardTireSearchInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      standardTireSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getFitmentTireProductSearch = ({
  frontTireSize,
  nearByStoreCodes,
  pageNum,
  searchOption,
  searchQuery,
  storeCode,
  treadwell,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.standardTireSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.standardTireSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.standardTireSearch?.results,
    recommendationAvailable:
      data?.fitmentSearch?.standardTireSearch?.recommendationAvailable,
    sorts: data?.fitmentSearch?.standardTireSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.standardTireSearch?.pagination?.totalNumberOfResults
  }),
  query: FITMENT_TIRE_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      searchOption,
      size: frontTireSize,
      treadwell
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const FITMENT_STAGGERED_TIRE_PRODUCT_SEARCH = gql`
  query FitmentStaggeredTireProductSearch(
    $vehicleInfo: VehicleInput!
    $searchInput: StaggeredTireSearchInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      staggeredTireSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getFitmentStaggeredTireProductSearch = ({
  frontTireSize,
  nearByStoreCodes,
  pageNum,
  rearTireSize,
  searchOption,
  searchQuery,
  staggeredSearchType,
  storeCode,
  treadwell,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.staggeredTireSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.staggeredTireSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.staggeredTireSearch?.results,
    recommendationAvailable:
      data?.fitmentSearch?.staggeredTireSearch?.recommendationAvailable,
    sorts: data?.fitmentSearch?.staggeredTireSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.staggeredTireSearch?.pagination?.totalNumberOfResults
  }),
  query: FITMENT_STAGGERED_TIRE_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      front: frontTireSize,
      nearByStoreCodes,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rear: rearTireSize,
      searchOption,
      staggeredType: staggeredSearchType,
      treadwell
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const PRODUCT_TYPE_PARAMS = {
  [PRODUCT_TYPES.ACCESSORIES]: 'ACCESSORY',
  [PRODUCT_TYPES.WHEELS]: 'WHEEL',
  [PRODUCT_TYPES.TIRES]: 'TIRE',
  ALL: 'ALL'
};

const standardSearchResult = gql`
  fragment standardSearchResult on ProductSearchResult {
    facets {
      ...facetsFragment
    }
    pagination {
      currentPage
      numberOfPages
      pageSize
      totalNumberOfResults
    }
    results {
      ...associatedSetFragment
      ...productFragment
    }
    sorts {
      code
      name
      selected
    }
    recommendationAvailable
  }
  ${associatedSetFragment}
  ${facetsFragment}
  ${productFragment}
`;

const STANDARD_SEARCH = gql`
  query StandardSearchQuery(
    $search: ProductSearchInput!
    $vehicleInfo: VehicleInput
    $storeCode: String!
  ) {
    productSearch {
      standardSearchQuery(
        search: $search
        vehicleInfo: $vehicleInfo
        storeCode: $storeCode
      ) {
        ...standardSearchResult
      }
    }
  }

  ${standardSearchResult}
`;

export const getStandardProductSearch = ({
  initialFacets,
  initialFilters,
  nearByStoreCodes,
  pageNum,
  productSetSearchType,
  productType,
  queryType,
  searchQuery,
  searchText,
  selectedSort,
  shouldApplyFallback = true,
  storeCode,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.productSearch?.standardSearchQuery?.facets,
    numberOfPages:
      data?.productSearch?.standardSearchQuery?.pagination?.numberOfPages,
    products: data?.productSearch?.standardSearchQuery?.results,
    recommendationAvailable:
      data?.productSearch?.standardSearchQuery?.recommendationAvailable,
    sorts: data?.productSearch?.standardSearchQuery?.sorts,
    totalNumberOfResults:
      data?.productSearch?.standardSearchQuery?.pagination?.totalNumberOfResults
  }),

  query: STANDARD_SEARCH,
  variables: {
    search: {
      applyFallBack: shouldApplyFallback,
      freeText: searchText,
      initialFacets,
      initialFilters,
      nearByStoreCodes,
      page: {
        pageNumber: pageNum || 0,
        pageSize: PAGE_SIZE,
        //This is a fallback for sort when the query arg is not present
        sort: searchText ? 'RELEVANCE' : selectedSort
      },
      productType:
        PRODUCT_TYPE_PARAMS[productType] || PRODUCT_TYPE_PARAMS['ALL'],
      query: searchQuery || '',
      queryType,
      wiperPosition: productSetSearchType
    },
    storeCode: storeCode,
    vehicleInfo: vehicleInput
  }
});

const STAGGERED_SEARCH = gql`
  query StaggeredSearchQuery(
    $search: StaggeredProductSearchInput!
    $vehicleInfo: VehicleInput
    $storeCode: String!
  ) {
    productSearch {
      staggeredSearchQuery(
        search: $search
        vehicleInfo: $vehicleInfo
        storeCode: $storeCode
      ) {
        ...productSearchResult
      }
    }
  }

  ${productSearchResult}
`;

export const getStaggeredProductSearch = ({
  initialFacets,
  initialFilters,
  nearByStoreCodes,
  pageNum,
  productType,
  queryType,
  searchQuery,
  searchText,
  selectedSort,
  staggeredSearchType,
  storeCode,
  vehicleInput
}) => ({
  mapResponse: data => ({
    filters: data?.productSearch?.staggeredSearchQuery?.facets,
    numberOfPages:
      data?.productSearch?.staggeredSearchQuery?.pagination?.numberOfPages,
    products: data?.productSearch?.staggeredSearchQuery?.results,
    recommendationAvailable:
      data?.productSearch?.staggeredSearchQuery?.recommendationAvailable,
    sorts: data?.productSearch?.staggeredSearchQuery?.sorts,
    totalNumberOfResults:
      data?.productSearch?.staggeredSearchQuery?.pagination
        ?.totalNumberOfResults
  }),

  query: STAGGERED_SEARCH,
  variables: {
    search: {
      applyFallBack: false,
      freeText: searchText,
      initialFacets,
      initialFilters,
      nearByStoreCodes,
      page: {
        pageNumber: pageNum || 0,
        pageSize: PAGE_SIZE,
        //This is a fallback for sort when the query arg is not present
        sort: searchText ? 'RELEVANCE' : selectedSort
      },
      productType:
        PRODUCT_TYPE_PARAMS[productType] || PRODUCT_TYPE_PARAMS['ALL'],
      query: searchQuery || '',
      queryType,
      staggeredType: staggeredSearchType
    },
    storeCode: storeCode,
    vehicleInfo: vehicleInput
  }
});
